import type { SCHEDULING_MODE } from "src/sections/standards/components/scheduling-mode";

import type { SiteDTO } from "./site.dto";
import type { SiteType } from "../siteType";
import type { CustomerDTO } from '../customer';
import type { StandardDTO } from "./standard.dto";
import type { InspectorDTO } from "./inspector.dto";
import type { ActivityCompetencyDTO } from "./competency.dto";
import type { AppointmentDTO } from "./appointment.dto";
import type { ActivityInspectorDTO } from "./activity-inspector.dto";
import { IProjectRole } from "../project-role";
import { IAccreditationBody } from "../accreditation-body";
import type { ActivityPreferredDateDto } from './activity-preferred-date.dto';
import type { BlackoutDatesDTO } from './blackout-dates';
import type { ProjectAuditTypeDTO } from './project-standard.dto';

export type ActivitySplitPartDTO = {
  id: number;
  partIndex: number;
  durationDays: number;
};

export enum ActivityDependencyBoundType {
  MIN = 'MIN',
  MAX = 'MAX',
}

export enum ActivityDependencyDirection {
  BEFORE = 'BEFORE',
  AFTER = 'AFTER',
}

export enum ActivityDependencyDurationType {
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

export interface ActivityDependencyCriterion {
  type: ActivityDependencyBoundType;
  direction: ActivityDependencyDirection;
  duration: number;
  durationType: ActivityDependencyDurationType;
}

export interface ActivityDependencySummaryDTO {
  id: number;
  name: string;
  site?: SiteDTO;
  criteria?: ActivityDependencyCriterion[];
}

export enum ActivityStatus {
  CANCELLED = 'cancelled',
  CREATED = 'created',
  DRAFTED = 'drafted',
  ALLOCATED = 'allocated',
  SCHEDULED = 'scheduled',
  ACCEPTED = 'accepted',
  COMPLETED = 'completed',
  REQUESTED = 'requested',
  PRE_REQUESTED = 'pre_requested',
  PRE_ACCEPTED = 'pre_accepted',
  PRE_SCHEDULED = 'pre_scheduled',
  REJECTED = 'rejected',
}

export const PRE_REQUESTED_ONWARDS: ActivityStatus[] = [
  ActivityStatus.PRE_REQUESTED,
  ActivityStatus.PRE_ACCEPTED,
  ActivityStatus.PRE_SCHEDULED,
  ActivityStatus.REQUESTED,
  ActivityStatus.ACCEPTED,
  ActivityStatus.SCHEDULED,
  ActivityStatus.COMPLETED,
];

/** Statuses where project roles cannot be changed (allocated or beyond). */
export const ACTIVITY_STATUSES_WITH_FROZEN_PROJECT_ROLES: ActivityStatus[] = [
  ActivityStatus.ALLOCATED,
  ActivityStatus.PRE_REQUESTED,
  ActivityStatus.PRE_ACCEPTED,
  ActivityStatus.PRE_SCHEDULED,
  ActivityStatus.REQUESTED,
  ActivityStatus.ACCEPTED,
  ActivityStatus.SCHEDULED,
  ActivityStatus.COMPLETED,
  ActivityStatus.REJECTED,
  ActivityStatus.CANCELLED,
];

export function isProjectRoleEditable(status: ActivityStatus): boolean {
  return !ACTIVITY_STATUSES_WITH_FROZEN_PROJECT_ROLES.includes(status);
}

export const StatusMap: Record<string, string> = {
  created: 'Created',
  drafted: 'Drafted',
  allocated: 'Allocated',
  pre_requested: 'Pre Requested',
  pre_accepted: 'Pre Accepted',
  pre_scheduled: 'Pre Scheduled',
  requested: 'Requested',
  accepted: 'Accepted',
  scheduled: 'Scheduled',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  completed: 'Completed',
};

export interface ActivityStatusConfigEntry {
  /** Statuses allowed to transition INTO this status */
  allowedTransitionsFrom: ActivityStatus[];
  /** Auto-revert to DRAFTED when appointment move/split/combine occurs */
  isRevertableToDrafted: boolean;
}

export const ActivityStatusConfig: Record<
  ActivityStatus,
  ActivityStatusConfigEntry
> = {
  [ActivityStatus.CREATED]: {
    allowedTransitionsFrom: [],
    isRevertableToDrafted: false,
  },
  [ActivityStatus.DRAFTED]: {
    allowedTransitionsFrom: [
      ActivityStatus.CREATED,
      ActivityStatus.ALLOCATED,
      ActivityStatus.PRE_REQUESTED,
      ActivityStatus.PRE_ACCEPTED,
      ActivityStatus.PRE_SCHEDULED,
      ActivityStatus.REQUESTED,
      ActivityStatus.ACCEPTED,
      ActivityStatus.SCHEDULED,
    ],
    isRevertableToDrafted: false,
  },
  [ActivityStatus.ALLOCATED]: {
    allowedTransitionsFrom: [ActivityStatus.DRAFTED, ActivityStatus.PRE_REQUESTED, ActivityStatus.REQUESTED],
    isRevertableToDrafted: true,
  },
  [ActivityStatus.PRE_REQUESTED]: {
    allowedTransitionsFrom: [ActivityStatus.ALLOCATED],
    isRevertableToDrafted: true,
  },
  [ActivityStatus.PRE_ACCEPTED]: {
    allowedTransitionsFrom: [
      ActivityStatus.ALLOCATED,
      ActivityStatus.PRE_REQUESTED,
    ],
    isRevertableToDrafted: true,
  },
  [ActivityStatus.PRE_SCHEDULED]: {
    allowedTransitionsFrom: [
      ActivityStatus.ALLOCATED,
      ActivityStatus.PRE_REQUESTED,
      ActivityStatus.PRE_ACCEPTED,
    ],
    isRevertableToDrafted: true,
  },
  [ActivityStatus.REQUESTED]: {
    allowedTransitionsFrom: [
      ActivityStatus.ALLOCATED,
      ActivityStatus.PRE_REQUESTED,
      ActivityStatus.PRE_ACCEPTED,
      ActivityStatus.PRE_SCHEDULED,
    ],
    isRevertableToDrafted: true,
  },
  [ActivityStatus.ACCEPTED]: {
    allowedTransitionsFrom: [
      ActivityStatus.ALLOCATED,
      ActivityStatus.PRE_REQUESTED,
      ActivityStatus.PRE_ACCEPTED,
      ActivityStatus.PRE_SCHEDULED,
      ActivityStatus.REQUESTED,
    ],
    isRevertableToDrafted: true,
  },
  [ActivityStatus.SCHEDULED]: {
    allowedTransitionsFrom: [
      ActivityStatus.ALLOCATED,
      ActivityStatus.PRE_REQUESTED,
      ActivityStatus.PRE_ACCEPTED,
      ActivityStatus.PRE_SCHEDULED,
      ActivityStatus.REQUESTED,
      ActivityStatus.ACCEPTED,
    ],
    isRevertableToDrafted: true,
  },
  [ActivityStatus.COMPLETED]: {
    allowedTransitionsFrom: [
      ActivityStatus.ALLOCATED,
      ActivityStatus.PRE_REQUESTED,
      ActivityStatus.PRE_ACCEPTED,
      ActivityStatus.PRE_SCHEDULED,
      ActivityStatus.REQUESTED,
      ActivityStatus.ACCEPTED,
      ActivityStatus.SCHEDULED,
    ],
    isRevertableToDrafted: false,
  },
  [ActivityStatus.REJECTED]: {
    allowedTransitionsFrom: [
      ActivityStatus.ALLOCATED,
      ActivityStatus.PRE_REQUESTED,
      ActivityStatus.PRE_ACCEPTED,
      ActivityStatus.PRE_SCHEDULED,
      ActivityStatus.REQUESTED,
      ActivityStatus.ACCEPTED,
      ActivityStatus.SCHEDULED,
    ],
    isRevertableToDrafted: false,
  },
  [ActivityStatus.CANCELLED]: {
    allowedTransitionsFrom: [
      ActivityStatus.CREATED,
      ActivityStatus.DRAFTED,
      ActivityStatus.ALLOCATED,
      ActivityStatus.PRE_REQUESTED,
      ActivityStatus.PRE_ACCEPTED,
      ActivityStatus.PRE_SCHEDULED,
      ActivityStatus.REQUESTED,
      ActivityStatus.ACCEPTED,
      ActivityStatus.SCHEDULED,
      ActivityStatus.COMPLETED,
      ActivityStatus.REJECTED,
    ],
    isRevertableToDrafted: false,
  },
};

/** Auditor profile History tab: statuses excl. drafted, rejected, cancelled (Done statement). */
export const AUDITOR_HISTORY_ACTIVITY_STATUSES: ActivityStatus[] = (
  Object.values(ActivityStatus) as ActivityStatus[]
).filter(
  (status) =>
    status !== ActivityStatus.DRAFTED &&
    status !== ActivityStatus.REJECTED &&
    status !== ActivityStatus.CANCELLED,
);

/** Comma-separated query value for GET /activities (same shape as the visit list). */
export const AUDITOR_HISTORY_ACTIVITY_STATUSES_QUERY =
  AUDITOR_HISTORY_ACTIVITY_STATUSES.join(',');
// Invert allowedTransitionsFrom → outgoing transition map (same shape as before)
export const ActivityStatusTransitionMap: Record<ActivityStatus, ActivityStatus[]> = (() => {
  const map = {} as Record<ActivityStatus, ActivityStatus[]>;
  for (const status of Object.values(ActivityStatus)) {
    map[status] = [];
  }
  for (const [toStatus, config] of Object.entries(ActivityStatusConfig) as [
    ActivityStatus,
    ActivityStatusConfigEntry,
  ][]) {
    for (const fromStatus of config.allowedTransitionsFrom) {
      map[fromStatus].push(toStatus);
    }
  }
  return map;
})();

export const ACTIVITY_STATUSES_REVERTABLE_TO_DRAFTED: ActivityStatus[] = (
  Object.entries(ActivityStatusConfig) as [
    ActivityStatus,
    ActivityStatusConfigEntry,
  ][]
)
  .filter(([, config]) => config.isRevertableToDrafted)
  .map(([status]) => status);

export const isRevertableToDrafted = (status?: string): boolean =>
  !!status && (ActivityStatusConfig[status as ActivityStatus]?.isRevertableToDrafted ?? false);

export enum ActivityType {
  Visit = 'Visit',
  DeskService = 'Desk Service'
}

export enum Priority {
  HIGH = 'HIGH',
  NORMAL = 'NORMAL',
  LOW = 'LOW',
}

export const PRIORITY_MAP = {
  [Priority.HIGH]: 'High',
  [Priority.NORMAL]: 'Normal',
  [Priority.LOW]: 'Low',
};

export const PRIORITY_VALUES_MAP = {
  [Priority.HIGH]: 101,
  [Priority.NORMAL]: 102,
  [Priority.LOW]: 103,
};

export interface ActivityDTO {
  id: number;
  name: string;
  status: ActivityStatus;
  type: ActivityType;
  duration: number;
  description?: string;
  location?: string;
  isAllocated?: boolean;
  /** True while a booking-request (n8n) workflow is in flight; status changes are locked. */
  bookingRequestInProgress?: boolean;
  allocatedInspectors?: InspectorDTO[];
  scheduledStartDate?: string | null;
  activityCompetencies?: ActivityCompetencyDTO[];
  activityTemplateCompetencies?: ActivityCompetencyDTO[];
  standard?: StandardDTO;
  projectAuditTypeId?: number | null;
  projectAuditType?: ProjectAuditTypeDTO | null;
  site?: SiteDTO;
  customer?: CustomerDTO;
  projectId?: number;
  allocationId?: number | null;
  durationDays: number;
  dueDate?: string | Date;
  targetDate?: string | Date;
  startDate?: string | Date;
  appointments?: AppointmentDTO[];
  splitParts?: ActivitySplitPartDTO[];
  priority?: Priority;
  activityInspectors?: ActivityInspectorDTO[];
  siteVisitType?: SiteType;
  schedulingMode?: SCHEDULING_MODE;
  leadAuditor?: InspectorDTO;
  minimumRole?: IProjectRole;
  accreditationBodies?: IAccreditationBody[];
  /** Competency ids collectively satisfied by assigned inspectors (level and AB aware). */
  collectivelyCoveredCompetencyIds?: number[];
  preferredDates?: ActivityPreferredDateDto[];
  blackoutDates?: BlackoutDatesDTO[];
  dependsOnActivities?: ActivityDependencySummaryDTO[];
  /** Activities that depend on this one (reverse of dependsOnActivities). */
  dependentActivities?: ActivityDependencySummaryDTO[];
}

export interface ActivityAllocationTabProps {
  allocatedInspectors?: InspectorDTO[];
  onBreakInChangesSubmit?: (solutionId: number) => void;
  /** Returns `false` when save failed so the allocation UI can keep the pending edit state. */
  onSubmit?: (
    data: {
      inspectorId: number;
      schedule: boolean;
      preferredDate?: string;
      projectRoleId?: number;
      durationDays?: number;
    }[],
    activityId: number,
  ) => void | boolean | Promise<void | boolean>;
  showLoading?: boolean;
  type?: 'allocatedVisit' | 'visit';
}